<template>
  <section class="h-full mx-4" id="customersManagement">
    <BaseTable
      v-if="competitors"
      :headers="competitorHeaders"
      :list="competitors"
      :total-records="competitorsTotalCount"
      :items-per-page="competitorsItemsForPage"
      :selected-page="competitorsPageNumber"
      table-id="competitors-management"
      @refresh-data="onRefreshData"
      @deleteMultiselect="onDeleteMultiselect"
      @add-new="onAddNewCompetitor"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters"
      clickable
      @table-item-clicked="onItemClick">
      <template #actions="{ item }">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="item.isFromManager"
            color="red"
            :icon="IconTrashOutline"
            @action="deleteSingleConfirm(item as Competitor)" />
        </div>
      </template>
      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>
    <BaseModalConfirm
      v-if="confirmDeleteSingleModalActive"
      @cancel="confirmDeleteSingleModalActive = false"
      @confirm="onDeleteSingle()">
      <template #title>{{ $t('common.actions.delete') }}</template>
      <template #text> {{ $t('messages.info.deleteCascade') }}</template>
      <template #buttonText>{{ $t('common.actions.delete') }}</template>
    </BaseModalConfirm>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { getCompetitorsWithConditions, removeCompetitorsByIds } from '@/services/api/traders';
import { useToastStore } from '@/stores/toast';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';
import type { Competitor } from '@/types/Traders';
import type { TableFilterData } from '@/types/Base';
import router from '@/router';
import { v4 as uuidv4 } from 'uuid';
import { useI18n } from 'vue-i18n';
import { useStateStore } from '@/stores/state';

const { t: $t } = useI18n({ useScope: 'global' });
const toastStore = useToastStore();
const competitors = ref<Competitor[]>();
const competitorsTotalCount = ref<number>();
const competitorsItemsForPage = ref(50);
const competitorsPageNumber = ref(1);
const confirmDeleteSingleModalActive = ref(false);
const competitorToDeleteSingle = ref<Competitor>();
const filterIsActive = ref<boolean | undefined>(undefined);

const competitorHeaders = [
  { value: 'boid', text: $t('baseObject.boid'), type: 'text', active: true },
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  {
    value: 'competitorType.name',
    text: $t('competitor.competitorType'),
    type: 'text',
    active: true,
  },
  { value: 'trader.name', text: $t('competitor.traderName'), type: 'text', active: true },
  { value: 'trader.taxNumber', text: $t('competitor.traderTaxNumber'), type: 'text', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

type QueryParams = {
  page?: number;
  itemsPerPage: number;
  query?: string;
  sortColumn?: string;
  sortAscending?: boolean;
  isActive?: boolean;
};

const { getTableSelectedPage } = useStateStore();

onMounted(async () => {
  const storedTableData = getTableSelectedPage('competitors-management');
  if (storedTableData) {
    competitorsItemsForPage.value = storedTableData.itemsPerPage;
    competitorsPageNumber.value = storedTableData.page;
  }
  await loadData({
    itemsPerPage: competitorsItemsForPage.value,
    sortColumn: 'code',
    sortAscending: true,
    page: competitorsPageNumber.value,
  });
});

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value;
  onRefreshData(data);
}

async function onRefreshData(data: TableFilterData) {
  const sortColumn = data.column !== undefined ? competitorHeaders[data.column]?.value : 'id';
  const sortAscending = data.order === 'asc' || data.order === undefined;
  const params: QueryParams = {
    page: data.page || 1,
    itemsPerPage: data.itemsPerPage || 50,
    query: data.query || '',
    sortColumn: sortColumn,
    sortAscending: sortAscending,
    isActive: data.isActive,
  };

  await loadData(params);
}

async function loadData(params: QueryParams) {
  const res = await getCompetitorsWithConditions(params);
  if (res) {
    competitors.value = res.data;
    competitorsTotalCount.value = res.count;
  }
}

async function onDeleteSingle() {
  if (!competitorToDeleteSingle.value) return;
  confirmDeleteSingleModalActive.value = false;
  await deleteCompetitors([competitorToDeleteSingle.value.id]);
}

async function onDeleteMultiselect(items: Competitor[]) {
  const idsToDelete = items.filter((x) => x.isFromManager == true).map((item) => item.id);
  if (idsToDelete.length) await deleteCompetitors(idsToDelete);
}

async function deleteCompetitors(idsToDelete: string[]) {
  const res = await removeCompetitorsByIds(idsToDelete);
  if (!res && competitors.value)
    competitors.value = competitors.value.filter((type) => !idsToDelete.includes(type.id));
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

function deleteSingleConfirm(item: Competitor) {
  competitorToDeleteSingle.value = item;
  confirmDeleteSingleModalActive.value = true;
}

function onAddNewCompetitor() {
  router.push({ name: 'competitors-preview', params: { id: uuidv4() } });
}

function onItemClick(item: Competitor) {
  router.push({ name: 'competitors-preview', params: { id: item.id } });
}

function onResetFilters() {
  filterIsActive.value = false;
  onRefreshData({});
}
</script>
